import React, { useState } from 'react';
import { IoMdAdd } from 'react-icons/io';
import { TbSearch } from 'react-icons/tb';
import { FiMoreVertical } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { BsRobot } from 'react-icons/bs';
import useMonitoringList from './hooks/useMonitoringList';
import MonitoringCreateModal from './MonitoringCreateModal';
import MonitoringRiskLevelModal from './MonitoringRiskLevelModal';
import MonitoringHistoryDrawer from './MonitoringHistoryDrawer';
import MonitoringDeactivateDialog from './MonitoringDeactivateDialog';
import MonitoringChatModal from './MonitoringChatModal';
import NextMonitoringCell from './NextMonitoringCell';
import { parseLocalDate, formatLocalDateToBR } from '../../../utils/DateUtils';
import DateRangeFilter from './DateRangeFilter';
import SortableColumnHeader from './SortableColumnHeader';
import ExportDropdown from './ExportDropdown';
import SelectFilter from '../../organisms/SelectFilter';
import APaginationInline from '../../molecules/APaginationInline';
import { formatTaxId } from '../../../utils/taxIdUtils';
import { Classification, monitoringApi, MonitoringApiError } from '../../../services/monitoringApi';

type RiskLevel = 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';

const RISK_BADGE: Record<RiskLevel, { bg: string; label: string }> = {
    EXTREME: { bg: 'bg-red-700 text-white', label: 'Extremo' },
    HIGH: { bg: 'bg-red-500 text-white', label: 'Alto' },
    MEDIUM: { bg: 'bg-yellow-400 text-gray-900', label: 'Médio' },
    LOW: { bg: 'bg-green-500 text-white', label: 'Baixo' },
};

const formatDate = (dateStr: string | null) => formatLocalDateToBR(dateStr) || '—';

const formatNextMonitoring = (dateStr: string | null, active: boolean) => {
    if (!active) return { text: '—', badge: '', color: '' };
    if (!dateStr) return { text: 'Pendente', badge: 'Pendente', color: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300' };

    // AF-3447: Normalizar ambas as datas para meia-noite local antes de calcular
    // a diferença em dias. O backend agora retorna YYYY-MM-DD (date-only),
    // mas mantemos compatibilidade com timestamps completos (ISO com hora).
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const next = parseLocalDate(dateStr)!;
    const nextMidnight = new Date(next.getFullYear(), next.getMonth(), next.getDate());
    const diffDays = Math.round((nextMidnight.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
        return { text: formatDate(dateStr), badge: `${Math.abs(diffDays)}d atrasado`, color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
    }
    if (diffDays === 0) {
        return { text: formatDate(dateStr), badge: 'Hoje', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' };
    }
    if (diffDays <= 7) {
        return { text: formatDate(dateStr), badge: `${diffDays}d`, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    }
    return { text: formatDate(dateStr), badge: `${diffDays}d`, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
};

const MonitoringPage: React.FC = () => {
    const {
        rows, total, page, setPage, totalPages,
        isLoading, initialLoading, filters, updateFilter, clearFilters, refresh, toggleSort,
        awaitingByTaxId,
    } = useMonitoringList();

    // Modals and drawers
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showChatModal, setShowChatModal] = useState(false);
    const [riskModalItem, setRiskModalItem] = useState<Classification | null>(null);
    const [deactivateItem, setDeactivateItem] = useState<Classification | null>(null);
    const [historyItem, setHistoryItem] = useState<Classification | null>(null);
    const [notesItem, setNotesItem] = useState<Classification | null>(null);

    // Action menu
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    const toggleMenu = (id: string) => {
        setOpenMenuId(prev => prev === id ? null : id);
    };

    const handleReactivate = async (item: Classification) => {
        try {
            await monitoringApi.reactivate(item.person_id, { notes: 'Reativado pelo operador' });
            toast.success('Monitoramento reativado com sucesso');
            refresh();
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                toast.error(e.message);
            } else {
                toast.error('Erro ao reativar monitoramento');
            }
        }
    };

    const handleMonitorNow = async (item: Classification) => {
        try {
            const result = await monitoringApi.monitorNow(item.person_id);
            // AF-3581: o documento pode já estar aguardando retorno do provedor, caso em
            // que nada foi enviado. Sem este ramo caía no else e exibia "Consulta enviada",
            // anunciando sucesso de uma operação que não aconteceu.
            if (result?.data?.awaiting_provider) {
                toast.warning(result.data.message);
            } else if (result?.data?.already_monitored_today) {
                toast.info(result.data.message);
            } else {
                toast.success('Consulta enviada ao Inovamind. O resultado será processado automaticamente.');
            }
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                toast.error(e.message);
            } else {
                toast.error('Erro ao disparar monitoramento imediato');
            }
        }
    };

    const handleAction = (action: string, item: Classification) => {
        setOpenMenuId(null);
        switch (action) {
            case 'history':
                setHistoryItem(item);
                break;
            case 'risk':
                setRiskModalItem(item);
                break;
            case 'deactivate':
                setDeactivateItem(item);
                break;
            case 'notes':
                setNotesItem(item);
                break;
            case 'reactivate':
                handleReactivate(item);
                break;
            case 'monitorNow':
                handleMonitorNow(item);
                break;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                            Monitoramento PLD
                        </h1>
                        <nav className="text-sm text-gray-500 dark:text-gray-400">
                            PLD &rsaquo; Monitoramento
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <ExportDropdown filters={{
                            riskLevel: filters.riskLevel || undefined,
                            active: filters.active === '' ? undefined : filters.active === 'true',
                            search: filters.search ? filters.search.replace(/[.\-/\s]/g, '').toUpperCase() : undefined,
                            personName: filters.personName || undefined,
                            hasDivergence: filters.hasDivergence === '' ? undefined : filters.hasDivergence === 'true',
                            dateFrom: filters.dateFrom || undefined,
                            dateTo: filters.dateTo || undefined,
                            sortBy: filters.sortBy || undefined,
                            sortOrder: filters.sortOrder || undefined,
                        }} />
                        <button
                            onClick={() => setShowChatModal(true)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
                        >
                            <BsRobot size={16} />
                            Assistente IA
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                        >
                            <IoMdAdd size={18} />
                            Cadastrar
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-4 flex gap-2 flex-wrap items-end">
                    <div className="relative flex-1 max-w-[200px]">
                        <input
                            id="filter-document"
                            type="text"
                            className="block py-2.5 px-2 w-full text-sm text-gray-900 bg-white border border-gray-200 focus:outline-none focus:ring-0 focus:border-blue-600 peer appearance-none dark:text-white dark:bg-slate-800 dark:border-slate-600"
                            placeholder=" "
                            value={filters.search}
                            onChange={(e) => updateFilter('search', e.target.value)}
                            onKeyUp={(e) => { if (e.key === 'Enter') refresh(); }}
                        />
                        <label htmlFor="filter-document"
                            className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-1 dark:bg-slate-800 dark:text-gray-400">
                            CPF/CNPJ
                        </label>
                    </div>
                    <div className="relative flex-1 max-w-[280px]">
                        <input
                            id="filter-name"
                            type="text"
                            className="block py-2.5 px-2 w-full text-sm text-gray-900 bg-white border border-gray-200 focus:outline-none focus:ring-0 focus:border-blue-600 peer appearance-none dark:text-white dark:bg-slate-800 dark:border-slate-600"
                            placeholder=" "
                            value={filters.personName}
                            onChange={(e) => updateFilter('personName', e.target.value)}
                            onKeyUp={(e) => { if (e.key === 'Enter') refresh(); }}
                        />
                        <label htmlFor="filter-name"
                            className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-1 dark:bg-slate-800 dark:text-gray-400">
                            Nome / Razão Social
                        </label>
                    </div>
                    <SelectFilter
                        id="filter-risk"
                        label="Risco"
                        value={filters.riskLevel}
                        onChange={(v) => updateFilter('riskLevel', v)}
                        options={[
                            { value: '', label: 'Todos' },
                            { value: 'EXTREME', label: 'Extremo' },
                            { value: 'HIGH', label: 'Alto' },
                            { value: 'MEDIUM', label: 'Médio' },
                            { value: 'LOW', label: 'Baixo' },
                        ]}
                        className="w-40"
                    />
                    <SelectFilter
                        id="filter-status"
                        label="Status"
                        value={filters.active}
                        onChange={(v) => updateFilter('active', v)}
                        options={[
                            { value: '', label: 'Todos' },
                            { value: 'true', label: 'Ativos' },
                            { value: 'false', label: 'Inativos' },
                        ]}
                        className="w-40"
                    />
                    <SelectFilter
                        id="filter-divergence"
                        label="Divergência"
                        value={filters.hasDivergence}
                        onChange={(v) => updateFilter('hasDivergence', v)}
                        options={[
                            { value: '', label: 'Todos' },
                            { value: 'true', label: 'Com divergência' },
                            { value: 'false', label: 'Sem divergência' },
                        ]}
                        className="w-48"
                    />
                    <DateRangeFilter
                        dateFrom={filters.dateFrom}
                        dateTo={filters.dateTo}
                        onChange={(field, value) => updateFilter(field, value)}
                    />
                    <button
                        onClick={refresh}
                        className="px-4 py-2.5 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 text-sm"
                    >
                        <TbSearch size={16} />
                        Buscar
                    </button>
                    <button
                        onClick={clearFilters}
                        className="px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1 text-sm dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600 dark:hover:bg-slate-600"
                    >
                        Limpar
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden relative">
                    {initialLoading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando...</p>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                            <p className="text-lg mb-2">Nenhum documento monitorado</p>
                            <p className="text-sm">Clique em "Cadastrar" para adicionar um documento ao monitoramento.</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
                                    <thead className="bg-gray-50 dark:bg-slate-600">
                                        <tr>
                                            <SortableColumnHeader label="Nome/Razão Social" field="person_name" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <SortableColumnHeader label="Risco" field="risk_level" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <SortableColumnHeader label="Último Mon." field="last_analysis_at" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <SortableColumnHeader label="Próx. Mon." field="next_monitoring_at" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <SortableColumnHeader label="Cadastro" field="created_at" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <SortableColumnHeader label="Status" field="active" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <SortableColumnHeader label="Diverg." field="has_pending_divergence" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={toggleSort} />
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">
                                                Ações
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-slate-700 divide-y divide-gray-200 dark:divide-slate-600">
                                        {rows.map((row) => (
                                            <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {row.person_name || row.person_id}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {formatTaxId(row.tax_id || '') || row.person_id}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${RISK_BADGE[row.risk_level as RiskLevel]?.bg || 'bg-gray-200'}`}>
                                                        {RISK_BADGE[row.risk_level as RiskLevel]?.label || row.risk_level}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                                    {formatDate(row.last_analysis_at)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <NextMonitoringCell
                                                        info={formatNextMonitoring(row.next_monitoring_at, row.active)}
                                                        awaiting={row.tax_id ? awaitingByTaxId[row.tax_id] : undefined}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                                    {formatDate(row.created_at)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.active
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                                                        }`}>
                                                        {row.active ? 'Ativo' : 'Inativo'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    {row.has_pending_divergence && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" role="img" aria-label="Divergência pendente de análise" title="Divergência pendente de análise">
                                                            ⚠
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center relative">
                                                    <button
                                                        onClick={() => toggleMenu(row.id)}
                                                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-500"
                                                        aria-label="Ações"
                                                    >
                                                        <FiMoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                                                    </button>
                                                    {openMenuId === row.id && (
                                                        <div className="absolute right-6 top-12 z-30 w-44 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-gray-200 dark:border-slate-600">
                                                            <button
                                                                onClick={() => handleAction('history', row)}
                                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                                                            >
                                                                Ver Histórico
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction('notes', row)}
                                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                                                            >
                                                                Ver Observação
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction('risk', row)}
                                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                                                            >
                                                                Alterar Risco
                                                            </button>
                                                            {row.active && (
                                                                <button
                                                                    onClick={() => handleAction('monitorNow', row)}
                                                                    className="block w-full text-left px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                                                                >
                                                                    Monitorar Agora
                                                                </button>
                                                            )}
                                                            {row.active && (
                                                                <button
                                                                    onClick={() => handleAction('deactivate', row)}
                                                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                                                                >
                                                                    Desativar
                                                                </button>
                                                            )}
                                                            {!row.active && (
                                                                <button
                                                                    onClick={() => handleAction('reactivate', row)}
                                                                    className="block w-full text-left px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                                                                >
                                                                    Reativar
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="px-6 py-3 border-t border-gray-200 dark:border-slate-600 flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {total} registro{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
                                </span>
                                {totalPages > 1 && (
                                    <APaginationInline
                                        currentPage={page}
                                        totalPages={totalPages}
                                        onPageChange={setPage}
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {/* Loading overlay for subsequent requests */}
                    {isLoading && !initialLoading && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-slate-800/50 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals and Drawers */}
            <MonitoringCreateModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={refresh}
            />

            {riskModalItem && (
                <MonitoringRiskLevelModal
                    isOpen={!!riskModalItem}
                    item={riskModalItem}
                    onClose={() => setRiskModalItem(null)}
                    onSuccess={refresh}
                />
            )}

            {deactivateItem && (
                <MonitoringDeactivateDialog
                    isOpen={!!deactivateItem}
                    item={deactivateItem}
                    onClose={() => setDeactivateItem(null)}
                    onSuccess={refresh}
                />
            )}

            {historyItem && (
                <MonitoringHistoryDrawer
                    isOpen={!!historyItem}
                    item={historyItem}
                    onClose={() => setHistoryItem(null)}
                />
            )}

            {/* Notes dialog */}
            {notesItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-sm mx-4">
                        <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Observação</h3>
                            <button onClick={() => setNotesItem(null)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                {notesItem.notes || 'Nenhuma observação registrada.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Chat Assistant */}
            <MonitoringChatModal
                isOpen={showChatModal}
                onClose={() => setShowChatModal(false)}
            />
        </div>
    );
};

export default MonitoringPage;
